import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IsoStandard } from '../../constants/isoStandards';
import type { RootState } from '../../store/store';
import { fetchEnabledStandards, updateEnabledStandards } from './organizationStandardsApi';
import { clearPermissionRegistryCache, fetchPermissionRegistry } from '../platformPermissions/platformPermissionsApi';
import { logout } from '../auth/authSlice';

export const loadOrganizationStandards = createAsyncThunk(
  'organizationStandards/load',
  async () => {
    const [enabledStandards, registry] = await Promise.all([
      fetchEnabledStandards(),
      fetchPermissionRegistry(),
    ]);
    const permissionStandards: Record<string, IsoStandard[] | undefined> = {};
    for (const def of registry) {
      permissionStandards[def.key] = def.standards;
    }
    return { enabledStandards, permissionStandards };
  },
  {
    condition: (_, { getState }) => {
      const status = (getState() as RootState).organizationStandards.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const saveOrganizationStandards = createAsyncThunk(
  'organizationStandards/save',
  async (enabledStandards: IsoStandard[]) => updateEnabledStandards(enabledStandards),
);

interface OrganizationStandardsState {
  enabledStandards: IsoStandard[] | null;
  permissionStandards: Record<string, IsoStandard[] | undefined> | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  saveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: OrganizationStandardsState = {
  enabledStandards: null,
  permissionStandards: null,
  status: 'idle',
  saveStatus: 'idle',
  error: null,
};

const organizationStandardsSlice = createSlice({
  name: 'organizationStandards',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadOrganizationStandards.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadOrganizationStandards.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.enabledStandards = action.payload.enabledStandards;
        state.permissionStandards = action.payload.permissionStandards;
      })
      .addCase(loadOrganizationStandards.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Error al cargar normas';
      })
      .addCase(saveOrganizationStandards.pending, (state) => {
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(saveOrganizationStandards.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.enabledStandards = action.payload;
      })
      .addCase(saveOrganizationStandards.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.error.message ?? 'Error al guardar normas';
      })
      .addCase(logout, () => {
        clearPermissionRegistryCache();
        return initialState;
      });
  },
});

export const selectEnabledStandards = (state: {
  organizationStandards: OrganizationStandardsState;
}) => state.organizationStandards.enabledStandards;

export const selectPermissionStandardsMap = (state: {
  organizationStandards: OrganizationStandardsState;
}) => state.organizationStandards.permissionStandards;

export const selectOrganizationStandardsStatus = (state: {
  organizationStandards: OrganizationStandardsState;
}) => state.organizationStandards.status;

export const selectOrganizationStandardsSaveStatus = (state: {
  organizationStandards: OrganizationStandardsState;
}) => state.organizationStandards.saveStatus;

export default organizationStandardsSlice.reducer;
